import React, { useState, useEffect } from 'react';
import { FileText, ChevronLeft, ChevronRight, RefreshCw, Copy, Check, ZoomIn, ZoomOut, RotateCw } from 'lucide-react';
import './CancellationForm.css';

const CancellationForm = () => {
  const [files, setFiles] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [jsonData, setJsonData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [jsonLoading, setJsonLoading] = useState(false);
  const [error, setError] = useState('');
  const [copiedField, setCopiedField] = useState(null);
  const [pdfScale, setPdfScale] = useState(1);
  const [pdfRotation, setPdfRotation] = useState(0);

  // Fetch list of cancellation form files
  useEffect(() => {
    fetchFiles();
  }, []);

  // Fetch JSON when file is selected
  useEffect(() => {
    if (selectedFile) {
      fetchJsonData(selectedFile);
    }
  }, [selectedFile]);

  const fetchFiles = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/cancellation-forms');
      if (!response.ok) throw new Error('Failed to fetch cancellation forms');
      const data = await response.json();
      setFiles(data.files || []);
      if (data.files && data.files.length > 0) {
        setSelectedFile(data.files[0]);
      }
      setLoading(false);
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  const fetchJsonData = async (filename) => {
    try {
      setJsonLoading(true);
      const response = await fetch(`/api/cancellation-forms/${filename}/json`);
      if (!response.ok) throw new Error('Failed to fetch JSON data');
      const data = await response.json();
      setJsonData(data);
      setJsonLoading(false);
    } catch (err) {
      setJsonData(null);
      setJsonLoading(false);
    }
  };

  const getPdfUrl = (filename) => {
    return `/api/cancellation-forms/${filename}/pdf`;
  };

  const handleCopyAllJson = () => {
    if (jsonData) {
      navigator.clipboard.writeText(JSON.stringify(jsonData, null, 2));
      setCopiedField('__all__');
      setTimeout(() => setCopiedField(null), 2000);
    }
  };

  const handlePrevFile = () => {
    const currentIndex = files.indexOf(selectedFile);
    if (currentIndex > 0) {
      setSelectedFile(files[currentIndex - 1]);
    }
  };

  const handleNextFile = () => {
    const currentIndex = files.indexOf(selectedFile);
    if (currentIndex < files.length - 1) {
      setSelectedFile(files[currentIndex + 1]);
    }
  };

  if (loading) {
    return (
      <div className="cancellation-form-page loading">
        <div className="spinner"></div>
        <p>Loading cancellation forms...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="cancellation-form-page error">
        <div className="error-message">
          <h3>Error</h3>
          <p>{error}</p>
          <button onClick={fetchFiles} className="btn-retry">
            <RefreshCw size={16} /> Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="cancellation-form-page">
      {/* File Selector Header */}
      <div className="file-selector-header">
        <div className="file-nav">
          <button 
            className="nav-btn"
            onClick={handlePrevFile}
            disabled={files.indexOf(selectedFile) === 0}
          >
            <ChevronLeft size={20} />
          </button>
          
          <select 
            className="file-dropdown"
            value={selectedFile || ''}
            onChange={(e) => setSelectedFile(e.target.value)}
          >
            {files.map((file) => (
              <option key={file} value={file}>{file}</option>
            ))}
          </select>
          
          <button 
            className="nav-btn"
            onClick={handleNextFile}
            disabled={files.indexOf(selectedFile) === files.length - 1}
          >
            <ChevronRight size={20} />
          </button>
        </div>
        
        <div className="file-info">
          <FileText size={16} />
          <span>{files.length} forms available</span>
        </div>
      </div>

      {/* Split View Container */}
      <div className="split-view-container">
        {/* Left Panel: PDF Viewer */}
        <div className="pdf-panel">
          <div className="panel-header">
            <h3>PDF Document</h3>
            <div className="pdf-controls">
              <button 
                className="control-btn"
                onClick={() => setPdfScale(s => Math.max(0.5, s - 0.1))}
                title="Zoom Out"
              >
                <ZoomOut size={16} />
              </button>
              <span className="zoom-level">{Math.round(pdfScale * 100)}%</span>
              <button 
                className="control-btn"
                onClick={() => setPdfScale(s => Math.min(2, s + 0.1))}
                title="Zoom In"
              >
                <ZoomIn size={16} />
              </button>
              <button 
                className="control-btn"
                onClick={() => setPdfRotation(r => (r + 90) % 360)}
                title="Rotate"
              >
                <RotateCw size={16} />
              </button>
            </div>
          </div>
          <div className="pdf-viewer-wrapper">
            {selectedFile ? (
              <iframe
                src={getPdfUrl(selectedFile)}
                title="PDF Viewer"
                className="pdf-iframe"
                style={{
                  transform: `scale(${pdfScale}) rotate(${pdfRotation}deg)`,
                  transformOrigin: 'top left'
                }}
              />
            ) : (
              <div className="no-file-selected">
                <FileText size={48} />
                <p>Select a file to view</p>
              </div>
            )}
          </div>
        </div>

        {/* Right Panel: JSON Viewer */}
        <div className="json-panel">
          <div className="panel-header">
            <h3>Extracted JSON</h3>
            <div className="json-controls">
              <button 
                className={`copy-all-btn ${copiedField === '__all__' ? 'copied' : ''}`}
                onClick={handleCopyAllJson}
                title="Copy all JSON"
              >
                {copiedField === '__all__' ? <Check size={16} /> : <Copy size={16} />}
                {copiedField === '__all__' ? 'Copied!' : 'Copy JSON'}
              </button>
            </div>
          </div>
          
          <div className="json-viewer-wrapper">
            {jsonLoading ? (
              <div className="json-loading">
                <div className="spinner small"></div>
                <p>Loading JSON...</p>
              </div>
            ) : jsonData ? (
              <pre className="json-raw-content">
                {JSON.stringify(jsonData, null, 2)}
              </pre>
            ) : (
              <div className="no-json">
                <p>No JSON data available for this file</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CancellationForm;
